import { create } from 'zustand';
import { OnboardingData, ProductData, VisualContent, AdditionalOffers, VisualStyle, SalesAngle, ContextData, MultiFunnelData, FunnelConfig, FunnelSection } from '../types/onboarding';
import { axiosInstanceWebsite } from '../services/instance';
import { SectionTemplate, getSectionTemplates } from '../services/funnels';
import { DesignStructureResponse, DesignStructurePayload, DesignStructureRecommendedSection, selectDesignStructure, SalesAngleSelectResponse, selectSalesAngle } from '../services/designStructure/designStructureService';
import type { AdStyle } from '../feature/ads/cretives/components/modals/steps/adStudio/types';
import { templatesService } from '../feature/ads/cretives/services/templates.service';
import { MOCK_STYLES } from '../feature/ads/cretives/components/modals/steps/adStudio/stylesData';
import { creativesService } from '../feature/ads/cretives/services/creatives.service';

export enum UploadedMethodType {
  LINK = 'link',
  MANUAL = 'manual',
  PREVIOUS = 'previous',
  NONE = 'none',
}

const initialProductData: ProductData = {
  name: '',
  price: '',
  details: '',
  warranty: '30days',
  freeShipping: true,
  isManualEntry: false,
  productLink: '',
  customWarrantyDays: '',
  uploadedMethod: UploadedMethodType.LINK,
  variants: null,
};

const initialVisualContent: VisualContent = {
  imagesBase64: [],
  images: [],
  selectedAngle: [],
};

const initialAdditionalOffers: AdditionalOffers = {
  bumpOffer: {
    enabled: false,
    productName: '',
    price: '',
    image: "",
    url_images: "",
    description: '',
    isManualEntry: false,
    productLink: '',
    externalId: null,
    providerId: null,
    uploadedMethod: UploadedMethodType.NONE,
  },
  upsell: {
    enabled: false,
    productName: '',
    price: '',
    image: undefined,
    products: [],
  },
  downsell: {
    enabled: false,
    discountPercentage: '',
  },
};

const initialVisualStyle: VisualStyle = {
  colorPalette: '',
  brandName: '',
  useAiLogo: true,
};

const initialContextData: ContextData = {
  pain_detection: '',
  buyer_detection: '',
  recommended_sales_angles: [],
};

// Standard structural sections (always available regardless of design)
// Order: CTA1 right after cover (0), CTA2 mid-funnel, CTA3 late-funnel, Guarantee, FAQ last
const STANDARD_SECTIONS: FunnelSection[] = [
  { id: 'cta-1', name: 'Botón de compra 1', icon: 'shopping-cart', enabled: true, order: 0, isStandard: true },
  { id: 'warranty', name: 'Garantía', icon: 'shield', enabled: true, order: 1, isStandard: true },
  { id: 'cta-2', name: 'Botón de compra 2', icon: 'shopping-cart', enabled: true, order: 2, isStandard: true },
  { id: 'cta-3', name: 'Botón de compra 3', icon: 'shopping-cart', enabled: true, order: 3, isStandard: true },
  { id: 'faq', name: 'Preguntas frecuentes', icon: 'help-circle', enabled: true, order: 4, isStandard: true },
];

// Section types that should be hidden and always disabled in onboarding.
// Kept in code for potential future re-enablement.
const HIDDEN_SECTION_TYPES = new Set(['selector-cantidades']);

// Map section_type to a readable display name
const SECTION_TYPE_DISPLAY_NAMES: Record<string, string> = {
  'portada': 'Portada',
  'beneficios-destacados': 'Beneficios Destacados',
  'beneficios': 'Beneficios',
  'selector-cantidades': 'Selector de Cantidades',
  'antes-y-despues': 'Antes y Después',
  'visualizaciones-datos': 'Visualización de Datos',
  'hecho-de': 'Hecho de',
  'como-usar': 'Cómo Usar',
  'calificacion': 'Calificación',
  'testimonios': 'Testimonios',
  'objeciones': 'Objeciones',
  'tabla-comparativa': 'Tabla Comparativa',
  'ebook': 'Ebook / Lead Magnet',
};

type OnboardingStore = {
  data: OnboardingData;
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateProductData: (data: Partial<ProductData>) => void;
  updateVisualContent: (data: Partial<VisualContent>) => void;
  updateAdditionalOffers: (data: Partial<AdditionalOffers>) => void;
  updateVisualStyle: (data: Partial<VisualStyle>) => void;
  resetOnboardingData: () => void;
  salesAngles: SalesAngle[];
  hasFetchedSalesAngles: boolean;
  setSalesAngles: (salesAngles: SalesAngle[]) => void;
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) => void;
  previousProducts: any[];
  setPreviousProducts: (products: any[]) => void;
  isPreviousProductsLoading: boolean;
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => void;
  fetchPreviousProducts: () => Promise<void>;
  contextData: ContextData;
  updateContextData: (data: Partial<ContextData>) => void;
  priceErrors: { main?: string; bump?: string; upsell?: string };
  setPriceErrors: (errors: { main?: string; bump?: string; upsell?: string }) => void;
  clearPriceErrors: () => void;
  isCustomSectionsOnly: boolean;
  setIsCustomSectionsOnly: (value: boolean) => void;
  showCustomSectionsToggle: boolean;
  setShowCustomSectionsToggle: (value: boolean) => void;
  multiFunnelData: MultiFunnelData;
  setFunnelCount: (count: number) => void;
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => void;
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => void;
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => void;
  initializeFunnels: (count: number) => void;
  addFunnel: () => void;
  removeFunnel: (funnelIndex: number) => void;
  setFunnelDesign: (funnelIndex: number, templateId: string, templateType: string, recommendedSections?: DesignStructureRecommendedSection[]) => Promise<void>;
  isFetchingDesignSections: boolean;
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
  hasFetchedTemplates: boolean;
  fetchSectionTemplates: () => Promise<void>;
  // Design structure recommendation (AI-powered)
  designRecommendation: DesignStructureResponse | null;
  isFetchingDesignRecommendation: boolean;
  hasFetchedDesignRecommendation: boolean;
  fetchDesignRecommendation: () => Promise<void>;
  resetDesignRecommendation: () => void;
  // Sales angle recommendation (AI-powered)
  salesAngleRecommendation: SalesAngleSelectResponse | null;
  isFetchingSalesAngleRecommendation: boolean;
  hasFetchedSalesAngleRecommendation: boolean;
  fetchSalesAngleRecommendation: (rawAngles: { name: string; description: string }[]) => Promise<void>;
  // Ad creative templates
  adTemplates: AdStyle[];
  isFetchingAdTemplates: boolean;
  hasFetchedAdTemplates: boolean;
  fetchAdTemplates: () => Promise<void>;
  toggleFunnelAdTemplate: (funnelIndex: number, templateId: string) => void;
  // Estimated cost
  estimatedCost: { total_cost: number; funnels_cost: number; creatives_cost: number } | null;
  isFetchingEstimatedCost: boolean;
  fetchEstimatedCost: () => Promise<void>;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  data: {
    step: 1,
    productData: initialProductData,
    visualContent: initialVisualContent,
    additionalOffers: initialAdditionalOffers,
    visualStyle: initialVisualStyle,
    isManualEntry: false,
    productLink: '',
  },
  salesAngles: [],
  hasFetchedSalesAngles: false,
  previousProducts: [],
  isPreviousProductsLoading: false,
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => set({ isPreviousProductsLoading }),
  contextData: initialContextData,
  setStep: (step) => set((state) => ({ data: { ...state.data, step } })),
  nextStep: () => set((state) => {
    const maxStep = state.isCustomSectionsOnly ? 6 : 4;
    return { data: { ...state.data, step: Math.min(state.data.step + 1, maxStep) } };
  }),
  previousStep: () => set((state) => ({ data: { ...state.data, step: Math.max(state.data.step - 1, 1) } })),
  updateProductData: (productData) => 
    set((state) => {
      const newProductData = { ...state.data.productData, ...productData };
      return { 
        data: { 
          ...state.data, 
          productData: newProductData 
        }
      };
    }),
  updateVisualContent: (visualContent: Partial<VisualContent>) => 
    set((state) => {
      const newVisualContent = { ...state.data.visualContent, ...visualContent };
      return {
        data: { 
          ...state.data, 
          visualContent: newVisualContent
        }
      };
    }),
  updateAdditionalOffers: (additionalOffers) => 
    set((state) => {
      const updatedOffers = { ...state.data.additionalOffers };
      
      if (additionalOffers.bumpOffer) {
        updatedOffers.bumpOffer = {
          ...updatedOffers.bumpOffer,
          ...additionalOffers.bumpOffer
        };
      }
      
      if (additionalOffers.upsell) {
        updatedOffers.upsell = {
          ...updatedOffers.upsell,
          ...additionalOffers.upsell,
          // Handle nested products array if present
          products: additionalOffers.upsell.products || updatedOffers.upsell.products
        };
      }
      
      // Handle downsell specifically
      if (additionalOffers.downsell) {
        updatedOffers.downsell = {
          ...updatedOffers.downsell,
          ...additionalOffers.downsell
        };
      }
      
      return { 
        data: { 
          ...state.data, 
          additionalOffers: updatedOffers 
        } 
      };
    }),
  updateVisualStyle: (visualStyle) => 
    set((state) => ({ 
      data: { 
        ...state.data, 
        visualStyle: { ...state.data.visualStyle, ...visualStyle } 
      } 
    })),
  resetOnboardingData: () => set({
    data: {
      step: 1,
      productData: initialProductData,
      visualContent: initialVisualContent,
      additionalOffers: initialAdditionalOffers,
      visualStyle: initialVisualStyle,
      isManualEntry: false,
      productLink: '',
    },
    salesAngles: [],
    hasFetchedSalesAngles: false,
    previousProducts: [],
    isPreviousProductsLoading: false,
    contextData: initialContextData,
    priceErrors: {},
    isCustomSectionsOnly: false,
    multiFunnelData: { funnelCount: 3, funnels: [] },
    designRecommendation: null,
    isFetchingDesignRecommendation: false,
    hasFetchedDesignRecommendation: false,
    salesAngleRecommendation: null,
    isFetchingSalesAngleRecommendation: false,
    hasFetchedSalesAngleRecommendation: false,
    adTemplates: [],
    isFetchingAdTemplates: false,
    hasFetchedAdTemplates: false,
    estimatedCost: null,
    isFetchingEstimatedCost: false,
  }),
  priceErrors: {},
  setPriceErrors: (errors) => set({ priceErrors: errors }),
  clearPriceErrors: () => set({ priceErrors: {} }),
  isCustomSectionsOnly: false,
  setIsCustomSectionsOnly: (value: boolean) => set({ isCustomSectionsOnly: value }),
  showCustomSectionsToggle: false,
  setShowCustomSectionsToggle: (value: boolean) => set({ showCustomSectionsToggle: value }),
  multiFunnelData: { funnelCount: 3, funnels: [] },
  setFunnelCount: (count: number) => set((state) => ({
    multiFunnelData: { ...state.multiFunnelData, funnelCount: count }
  })),
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = { ...funnels[funnelIndex], ...config };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = {
        ...funnels[funnelIndex],
        sections: funnels[funnelIndex].sections.map(s =>
          s.id === sectionId ? { ...s, ...updates } : s
        )
      };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = { ...funnels[funnelIndex], sections };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  initializeFunnels: (count: number) => set((state) => {
    const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
    const funnels: FunnelConfig[] = Array.from({ length: count }, (_, i) => ({
      id: `funnel-${i + 1}`,
      name: `${brandName} ${i + 1}`,
      designTemplate: null,
      designTemplateType: null,
      sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
      selectedAngles: [],
      selectedAdTemplates: [],
    }));
    return { multiFunnelData: { funnelCount: count, funnels } };
  }),
  addFunnel: () => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length >= 5) return state;
    const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
    const newIndex = funnels.length + 1;
    const newFunnel: FunnelConfig = {
      id: `funnel-${Date.now()}`,
      name: `${brandName} ${newIndex}`,
      designTemplate: null,
      designTemplateType: null,
      sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
      selectedAngles: [],
      selectedAdTemplates: [],
    };
    const newFunnels = [...funnels, newFunnel];
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  removeFunnel: (funnelIndex: number) => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length <= 1) return state;
    const newFunnels = funnels.filter((_, i) => i !== funnelIndex);
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  isFetchingDesignSections: false,
  setFunnelDesign: async (funnelIndex: number, templateId: string, templateType: string, recommendedSections?: DesignStructureRecommendedSection[]) => {
    set({ isFetchingDesignSections: true });
    try {
      // Fetch all sections for this design type (excluding preview)
      const allSections = await getSectionTemplates('type', templateType);
      const designSections = allSections.filter(s => s.section_type !== 'preview');

      // Build a lookup for recommended sections (if provided)
      const hasRecommendation = recommendedSections && recommendedSections.length > 0;
      const recommendedTypesSet = hasRecommendation
        ? new Set(recommendedSections.map(rs => rs.section_type))
        : null;
      const recommendedOrderMap = hasRecommendation
        ? new Map(recommendedSections.map(rs => [rs.section_type, rs.order]))
        : null;

      // Convert API sections to FunnelSection format
      // If we have a recommendation, only enable sections that are recommended
      // Hidden section types are excluded entirely from the structure
      let apiSections: FunnelSection[] = designSections
        .filter((s) => !HIDDEN_SECTION_TYPES.has(s.section_type))
        .map((s, idx) => ({
          id: `design-${s.section_type}-${s.id}`,
          name: SECTION_TYPE_DISPLAY_NAMES[s.section_type] || s.section_type,
          icon: 'image',
          enabled: recommendedTypesSet ? recommendedTypesSet.has(s.section_type) : true,
          order: idx,
          sectionType: s.section_type,
          url: s.url || undefined,
          isStandard: false,
        }));

      // If we have a recommendation, sort sections by their recommended order
      // Enabled (recommended) sections first, sorted by order; disabled sections at the end
      if (recommendedOrderMap) {
        apiSections.sort((a, b) => {
          const aEnabled = a.enabled;
          const bEnabled = b.enabled;
          // Enabled sections come first
          if (aEnabled && !bEnabled) return -1;
          if (!aEnabled && bEnabled) return 1;
          // Both enabled: sort by recommended order
          if (aEnabled && bEnabled) {
            const orderA = a.sectionType ? (recommendedOrderMap.get(a.sectionType) ?? 999) : 999;
            const orderB = b.sectionType ? (recommendedOrderMap.get(b.sectionType) ?? 999) : 999;
            return orderA - orderB;
          }
          // Both disabled: keep original order
          return 0;
        });
      }

      // Interleave standard sections among design sections:
      // CTA1 right after cover (index 0), CTA2 mid-funnel, CTA3 late-funnel, Warranty, FAQ at the end
      const CTA1_INSERT_AFTER = 0;      // Right after the cover (1st design section)
      const WARRANTY_INSERT_AFTER = 1;  // After the 6th design section
      const CTA2_INSERT_AFTER = 3;      // After the 3rd design section (mid-funnel)
      const CTA3_INSERT_AFTER = 5;      // After the 5th design section (late-funnel)

      const cta1Section = STANDARD_SECTIONS.find(s => s.id === 'cta-1')!;
      const cta2Section = STANDARD_SECTIONS.find(s => s.id === 'cta-2')!;
      const cta3Section = STANDARD_SECTIONS.find(s => s.id === 'cta-3')!;
      const warrantySection = STANDARD_SECTIONS.find(s => s.id === 'warranty')!;
      const faqSection = STANDARD_SECTIONS.find(s => s.id === 'faq')!;

      const allFunnelSections: FunnelSection[] = [];
      let orderCounter = 0;

      for (let i = 0; i < apiSections.length; i++) {
        allFunnelSections.push({ ...apiSections[i], order: orderCounter++ });

        // Insert CTA1 right after the cover (1st design section)
        if (i === CTA1_INSERT_AFTER && apiSections.length > CTA1_INSERT_AFTER) {
          allFunnelSections.push({ ...cta1Section, order: orderCounter++ });
        }
        // Insert CTA2 after the 3rd design section
        if (i === CTA2_INSERT_AFTER && apiSections.length > CTA2_INSERT_AFTER) {
          allFunnelSections.push({ ...cta2Section, order: orderCounter++ });
        }
        // Insert CTA3 after the 5th design section
        if (i === CTA3_INSERT_AFTER && apiSections.length > CTA3_INSERT_AFTER) {
          allFunnelSections.push({ ...cta3Section, order: orderCounter++ });
        }
        // Insert Warranty after the 6th design section
        if (i === WARRANTY_INSERT_AFTER && apiSections.length > WARRANTY_INSERT_AFTER) {
          allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
        }
      }

      // If there weren't enough design sections, add missing sections at the end (before FAQ)
      if (apiSections.length <= CTA1_INSERT_AFTER) {
        allFunnelSections.push({ ...cta1Section, order: orderCounter++ });
      }
      if (apiSections.length <= CTA2_INSERT_AFTER) {
        allFunnelSections.push({ ...cta2Section, order: orderCounter++ });
      }
      if (apiSections.length <= CTA3_INSERT_AFTER) {
        allFunnelSections.push({ ...cta3Section, order: orderCounter++ });
      }
      if (apiSections.length <= WARRANTY_INSERT_AFTER) {
        allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
      }

      // FAQ always goes at the very end
      allFunnelSections.push({ ...faqSection, order: orderCounter++ });

      // Update the funnel config
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
            sections: allFunnelSections,
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    } catch (error) {
      console.error('Error fetching design sections:', error);
      // Still set the template even if section fetch fails
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    }
  },
  sectionTemplates: [],
  isFetchingTemplates: false,
  hasFetchedTemplates: false,
  fetchSectionTemplates: async () => {
    const state = get();
    if (state.hasFetchedTemplates || state.isFetchingTemplates) return;
    set({ isFetchingTemplates: true });
    try {
      const templates = await getSectionTemplates('section_type', 'preview');
      set({ sectionTemplates: templates, hasFetchedTemplates: true, isFetchingTemplates: false });
    } catch (error) {
      console.error('Error fetching section templates:', error);
      set({ isFetchingTemplates: false });
    }
  },
  setSalesAngles: (salesAngles: SalesAngle[]) =>
    set({ salesAngles }),
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) =>
    set({ hasFetchedSalesAngles }),
  updateContextData: (contextData) =>
    set((state) => ({
      contextData: { ...state.contextData, ...contextData }
    })),
  fetchPreviousProducts: async () => {
    const state = get();
    
    if (state.isPreviousProductsLoading) {
      return;
    }
    if (state.previousProducts.length === 0) {
      set({ isPreviousProductsLoading: true });
      try {
        const response = await axiosInstanceWebsite.get('/store/product');
        set({ 
          previousProducts: response.data.reverse() || [],
          isPreviousProductsLoading: false
        });
      } catch (err) {
        console.error('Error fetching previous products:', err);
        set({ 
          previousProducts: [],
          isPreviousProductsLoading: false 
        });
      }
    }
  },
  setPreviousProducts: (products) => set({ previousProducts: products }),
  // Design structure recommendation
  designRecommendation: null,
  isFetchingDesignRecommendation: false,
  hasFetchedDesignRecommendation: false,
  fetchDesignRecommendation: async () => {
    const state = get();
    if (state.isFetchingDesignRecommendation || state.hasFetchedDesignRecommendation) return;

    set({ isFetchingDesignRecommendation: true });

    try {
      const { productData } = state.data;
      const { visualContent } = state.data;
      let payload: DesignStructurePayload;

      // If the product was selected from "tus productos", send product_id
      if (productData.uploadedMethod === UploadedMethodType.PREVIOUS && productData.externalId) {
        payload = { product_id: productData.externalId };
      } else {
        // Otherwise send product info (name, description, images)
        payload = {
          product_name: productData.name,
          product_description: productData.details,
          product_images: visualContent.images || [],
        };
      }

      const response = await selectDesignStructure(payload);
      set({
        designRecommendation: response,
        isFetchingDesignRecommendation: false,
        hasFetchedDesignRecommendation: true,
      });
    } catch (error) {
      console.error('Error fetching design structure recommendation:', error);
      set({
        isFetchingDesignRecommendation: false,
        hasFetchedDesignRecommendation: true,
      });
    }
  },
  resetDesignRecommendation: () => set({
    designRecommendation: null,
    isFetchingDesignRecommendation: false,
    hasFetchedDesignRecommendation: false,
  }),
  // Sales angle recommendation
  salesAngleRecommendation: null,
  isFetchingSalesAngleRecommendation: false,
  hasFetchedSalesAngleRecommendation: false,
  fetchSalesAngleRecommendation: async (rawAngles: { name: string; description: string }[]) => {
    const state = get();
    if (state.isFetchingSalesAngleRecommendation || state.hasFetchedSalesAngleRecommendation) return;

    set({ isFetchingSalesAngleRecommendation: true });

    try {
      const { productData, visualContent } = state.data;

      const payload = {
        angles: rawAngles,
        design_structure: state.designRecommendation || null,
        product_name: productData.name,
        product_description: productData.details,
        product_images: visualContent.images || [],
      };

      const response = await selectSalesAngle(payload);

      // Auto-select the recommended angle in visualContent.selectedAngle
      const matchingAngle = state.salesAngles.find(
        (a) => a.id === response.selected_angle || a.title === response.selected_angle
      );

      if (matchingAngle) {
        set((s) => ({
          salesAngleRecommendation: response,
          isFetchingSalesAngleRecommendation: false,
          hasFetchedSalesAngleRecommendation: true,
          data: {
            ...s.data,
            visualContent: {
              ...s.data.visualContent,
              selectedAngle: [{ name: matchingAngle.id, description: matchingAngle.description }],
            },
          },
        }));
      } else {
        set({
          salesAngleRecommendation: response,
          isFetchingSalesAngleRecommendation: false,
          hasFetchedSalesAngleRecommendation: true,
        });
      }
    } catch (error) {
      console.error('Error fetching sales angle recommendation:', error);
      set({
        isFetchingSalesAngleRecommendation: false,
        hasFetchedSalesAngleRecommendation: true,
      });
    }
  },
  // Ad creative templates
  adTemplates: [],
  isFetchingAdTemplates: false,
  hasFetchedAdTemplates: false,
  fetchAdTemplates: async () => {
    const state = get();
    if (state.hasFetchedAdTemplates || state.isFetchingAdTemplates) return;
    set({ isFetchingAdTemplates: true });
    try {
      const templates = await templatesService.getTemplates();
      // Filter out custom upload entries — not relevant for onboarding
      const filtered = templates.filter(t => !t.isCustomUpload);
      set({ adTemplates: filtered, hasFetchedAdTemplates: true, isFetchingAdTemplates: false });
    } catch (error) {
      console.error('Error fetching ad templates:', error);
      // Fall back to mock data so the tab is never empty
      const fallback = MOCK_STYLES.filter(t => !t.isCustomUpload);
      set({ adTemplates: fallback, hasFetchedAdTemplates: true, isFetchingAdTemplates: false });
    }
  },
  toggleFunnelAdTemplate: (funnelIndex: number, templateId: string) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (!funnels[funnelIndex]) return state;
    const current = funnels[funnelIndex].selectedAdTemplates || [];
    const isSelected = current.includes(templateId);
    const maxTemplates = 10;
    let updated: string[];
    if (isSelected) {
      updated = current.filter(id => id !== templateId);
    } else if (current.length < maxTemplates) {
      updated = [...current, templateId];
    } else {
      return state;
    }
    funnels[funnelIndex] = { ...funnels[funnelIndex], selectedAdTemplates: updated };
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  estimatedCost: null,
  isFetchingEstimatedCost: false,
  fetchEstimatedCost: async () => {
    const state = get();
    if (state.isFetchingEstimatedCost) return;
    set({ isFetchingEstimatedCost: true });
    try {
      const funnels = state.multiFunnelData.funnels;
      const funnelsCount = funnels.length;
      const creativesCount = funnels.reduce(
        (sum, f) => sum + (f.selectedAdTemplates?.length || 0),
        0
      );
      const result = await creativesService.estimateCost(creativesCount, funnelsCount);
      set({ estimatedCost: result, isFetchingEstimatedCost: false });
    } catch (error) {
      console.error('Error fetching estimated cost:', error);
      set({ isFetchingEstimatedCost: false });
    }
  },
}));